import * as React from 'react';

import {SearchBar} from '../search-bar/search-bar';

import type {LinkConfig} from './link-item';
import {NavItems} from './nav-items';

import styles from './sidebar-nav.module.css';

export type NavItem = {
    id: string;
    label?: string;
    links: LinkConfig[];
    shouldHide?: boolean;
    footer?: React.ReactNode;
};

type Props = {
    filteredDisplayLinkIds: string[];
    navItems: NavItem[];
    navItemsBeforeHeaderComponent?: NavItem[];
    includeSearch?: boolean;
    searchPlaceholder: string;
    headerComponent?: React.ReactNode;
    footer?: React.ReactNode;
    handleSearchChange?: (searchString: string) => void;
    isFullHeight?: boolean;
};

export function SidebarNavItems(props: Props) {
    const [searchString, setSearchString] = React.useState('');
    const [pageYOffset, setPageYOffset] = React.useState(0);
    const [containerYOffset, setContainerYOffset] = React.useState(0);

    const containerRef = React.useRef<HTMLDivElement | null | undefined>(null);

    React.useEffect(() => {
        window.addEventListener('scroll', () => {
            setPageYOffset(window.pageYOffset);
        });

        if (containerRef.current) {
            // The sidebar may not be at the top of the page due to reasons like a trial banner, or the parent component's layout.
            // To prevent users from being able to infinitely scroll, we need to account for this offset.
            setContainerYOffset(containerRef.current.getBoundingClientRect().top);
        }
    }, []);

    return (
        <div
            // @ts-expect-error
            ref={containerRef}
            className={props.headerComponent ? styles['settings-container'] : styles.container}
        >
            <div
                style={{height: `calc(100vh - ${containerYOffset}px + ${pageYOffset}px)`}}
                className={
                    props.isFullHeight
                        ? styles['sidebar-container--full-height']
                        : styles['sidebar-container']
                }
            >
                {props.includeSearch && (
                    <div className={styles.search}>
                        <SearchBar
                            value={searchString}
                            placeholder={props.searchPlaceholder}
                            onChange={(value) => {
                                props.handleSearchChange?.(value);
                                setSearchString(value);
                            }}
                            isClearable={true}
                            hasBorder={true}
                            styleOptions={{height: 32}}
                            isSmall={true}
                        />
                    </div>
                )}
                {props.navItemsBeforeHeaderComponent && (
                    <NavItems
                        navItems={props.navItemsBeforeHeaderComponent}
                        filteredDisplayLinkIds={props.filteredDisplayLinkIds}
                        noPadding={true}
                    />
                )}
                {props.headerComponent && (
                    <div className={props.navItemsBeforeHeaderComponent ? 'mt-24' : 'mt-8'}>
                        {props.headerComponent}
                    </div>
                )}
                <NavItems
                    navItems={props.navItems}
                    filteredDisplayLinkIds={props.filteredDisplayLinkIds}
                    isBelowHeaderComponent={Boolean(props.headerComponent)}
                />
            </div>
            {props.footer && <div className={styles.footer}>{props.footer}</div>}
        </div>
    );
}
